import React, { useEffect, useState } from "react";
import { talentPool, getCandidateProfile } from "@/lib/companyApi";
import { createThread } from "@/lib/messagesApi";
import ScoreRing from "@/components/wishire/ScoreRing";
import { Search, Sparkles, Mail, Loader2, X, AlertCircle, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function TalentPool() {
  const [role, setRole] = useState("");
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileModal, setProfileModal] = useState(null); // user_id

  const load = async (r) => {
    setLoading(true);
    try {
      const { data } = await talentPool(r || undefined, 50);
      setPool(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  const submit = (e) => {
    e.preventDefault();
    load(role);
  };

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <span className="overline">Talent Pool</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            Ranked candidates.{" "}
            <span className="italic text-stone-500">Top of pile first.</span>
          </h1>
        </div>
        <form onSubmit={submit} className="flex items-center gap-2 max-w-md w-full">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              data-testid="talent-search"
              placeholder="Filter by target role (e.g. Senior Backend)"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
            />
          </div>
          <button type="submit" data-testid="talent-search-submit" className="btn-primary text-sm">
            Filter
          </button>
        </form>
      </div>

      {loading ? (
        <p className="text-stone-500 inline-flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Ranking candidates…
        </p>
      ) : pool.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-10 text-center text-stone-600">
          <Sparkles className="mx-auto mb-3" size={20} />
          No candidates match this role yet. Try a broader role title.
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {pool.map((c) => (
            <CandidateCard
              key={c.user_id}
              candidate={c}
              onViewProfile={() => setProfileModal(c.user_id)}
            />
          ))}

        </ul>
      )}

      {profileModal && (
        <CandidateProfileModal
          userId={profileModal}
          onClose={() => setProfileModal(null)}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// Candidate Card
// ----------------------------------------------------------------
function CandidateCard({ candidate: c, onViewProfile }) {
  return (
    <li
      data-testid={`talent-${c.user_id}`}
      className="rounded-3xl border border-stone-200 bg-white p-6 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(28,25,23,0.25)] transition-all duration-300"
    >
      {/* Top row: avatar + name + score ring */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          {c.picture ? (
            <img src={c.picture} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center text-sm font-medium shrink-0">
              {(c.name || "?")[0]}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-semibold text-stone-900 truncate" title={c.name}>{c.name}</p>
            <p className="text-stone-500 text-xs truncate mt-0.5">{c.target_role || "—"}</p>
          </div>
        </div>
        <div className="flex flex-col items-center shrink-0">
          <ScoreRing value={c.wishire_score} size={72} />
          <span className="text-[9px] font-mono uppercase tracking-widest text-stone-400 mt-1">Wishire Score</span>
        </div>
      </div>

      {/* Score tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Tag value={c.resume_score} label="Resume" present={c.has_resume} />
        <Tag value={c.interview_score} label="Interview" present={c.has_interview} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-stone-100">
        <button
          onClick={onViewProfile}
          data-testid={`talent-profile-${c.user_id}`}
          className="flex-1 text-sm py-2 rounded-full bg-stone-900 text-white hover:bg-stone-700 transition-colors text-center font-medium"
        >
          View profile
        </button>
        <a
          href={`mailto:${c.email}?subject=From%20Wishire`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors text-sm"
          data-testid={`talent-email-${c.user_id}`}
        >
          <Mail size={13} /> Email
        </a>
      </div>
    </li>
  );
}

// ----------------------------------------------------------------
// Candidate Profile Modal (no job context — talent pool)
// ----------------------------------------------------------------
function CandidateProfileModal({ userId, onClose }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [messaging, setMessaging] = useState(false);

  const handleMessage = async () => {
    setMessaging(true);
    try {
      await createThread(userId, null, null);
      navigate("/company/messages");
    } catch (e) { setMessaging(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getCandidateProfile(userId);
        setProfile(data);
      } catch (e) {}
      finally { setLoading(false); }
    })();
  }, [userId]);

  const c = profile?.candidate || {};
  const resume = profile?.resume;
  const interview = profile?.interview;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF7] rounded-3xl border border-stone-200 shadow-2xl">

        {/* Header */}
        <div className="sticky top-0 bg-[#FDFBF7] border-b border-stone-200 px-7 py-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            {c.picture ? (
              <img src={c.picture} alt="" className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center font-medium text-sm">
                {(c.name || "?")[0]}
              </div>
            )}
            <div>
              <h2 className="font-display text-xl">{c.name || "Candidate"}</h2>
              <p className="text-stone-500 text-sm">{c.headline || c.target_role || c.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleMessage}
              disabled={messaging}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C2410C] text-white hover:bg-orange-700 disabled:opacity-60 transition-colors text-xs font-medium"
            >
              {messaging ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Message
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-stone-500">
            <Loader2 className="animate-spin mx-auto mb-2" size={20} />
            Loading candidate profile…
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="px-7 pt-5">
              <div className="inline-flex p-1 rounded-full border border-stone-300 bg-white mb-5">
                {[["overview", "Overview"], ["resume", "Resume"], ["interview", "Interview"]].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      tab === k ? "bg-stone-900 text-stone-50" : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-7 pb-7">

              {/* Overview */}
              {tab === "overview" && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <InfoBox label="Email" value={c.email} />
                    <InfoBox label="City" value={c.city || "—"} />
                    <InfoBox label="Experience" value={c.years_experience ? `${c.years_experience} yrs` : "—"} />
                    <InfoBox label="Target Role" value={c.target_role || "—"} />
                  </div>

                  {/* Scores */}
                  <div className="rounded-2xl bg-white border border-stone-200 p-5">
                    <p className="overline mb-3">Wishire Scores</p>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="font-display text-3xl" style={{ color: scoreColor(c.latest_resume_score) }}>
                          {c.latest_resume_score || "—"}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">Resume</p>
                      </div>
                      <div>
                        <p className="font-display text-3xl" style={{ color: scoreColor(c.latest_interview_score) }}>
                          {c.latest_interview_score || "—"}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">Interview</p>
                      </div>
                      <div>
                        <p className="font-display text-3xl" style={{ color: scoreColor(profile?.application?.wishire_score) }}>
                          {profile?.application?.wishire_score || "—"}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">Composite</p>
                      </div>
                    </div>
                  </div>

                  {/* Skills */}
                  {c.skills?.length > 0 && (
                    <div>
                      <p className="overline mb-2">Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {c.skills.map((s, i) => (
                          <span key={i} className="px-3 py-1 rounded-full bg-stone-100 border border-stone-200 text-sm">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reach out CTA */}
                  <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-900">Interested in this candidate?</p>
                      <p className="text-xs text-amber-700 mt-0.5">Reach out directly via email.</p>
                    </div>
                    <a
                      href={`mailto:${c.email}?subject=Opportunity%20via%20Wishire`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 text-amber-900 hover:bg-amber-300 transition-colors text-sm font-medium"
                    >
                      <Mail size={14} /> Email candidate
                    </a>
                  </div>
                </div>
              )}

              {/* Resume */}
              {tab === "resume" && (
                <div>
                  {!resume ? (
                    <div className="rounded-2xl bg-stone-50 border border-stone-200 p-8 text-center text-stone-500">
                      <AlertCircle size={20} className="mx-auto mb-2" />
                      No resume submitted yet.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <ScoreCard label="Overall" score={resume.result?.overall_score} />
                        <ScoreCard label="ATS" score={resume.result?.ats_score} />
                        <ScoreCard label="Keywords" score={resume.result?.keyword_match} />
                      </div>
                      <div className="rounded-2xl bg-white border border-stone-200 p-5">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-3 border ${
                          resume.result?.role_fit === "Strong fit" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                            : resume.result?.role_fit === "Good fit" ? "bg-blue-50 text-blue-800 border-blue-200"
                            : "bg-amber-50 text-amber-800 border-amber-200"
                        }`}>{resume.result?.role_fit || "—"}</span>
                        <p className="text-sm text-stone-600 italic mb-4">{resume.result?.summary}</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-semibold text-emerald-700 mb-2">Strengths</p>
                            <ul className="space-y-1">
                              {resume.result?.strengths?.map((s, i) => (
                                <li key={i} className="text-xs text-stone-600">• {s}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-red-700 mb-2">Gaps</p>
                            <ul className="space-y-1">
                              {resume.result?.gaps?.map((s, i) => (
                                <li key={i} className="text-xs text-stone-600">• {s}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Interview */}
              {tab === "interview" && (
                <div>
                  {!interview ? (
                    <div className="rounded-2xl bg-stone-50 border border-stone-200 p-8 text-center text-stone-500">
                      <AlertCircle size={20} className="mx-auto mb-2" />
                      No interview completed yet.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <ScoreCard label="Overall" score={interview.result?.overall_score} />
                        <ScoreCard label="Clarity" score={interview.result?.clarity} />
                        <ScoreCard label="Depth" score={interview.result?.depth} />
                        <ScoreCard label="Communication" score={interview.result?.communication} />
                      </div>
                      <div className="rounded-2xl bg-white border border-stone-200 p-5">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-3 border ${
                          (interview.result?.verdict || "").toLowerCase().includes("hire")
                            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                            : "bg-red-50 text-red-800 border-red-200"
                        }`}>{interview.result?.verdict || "—"}</span>
                        <p className="text-sm text-stone-700 leading-relaxed">{interview.result?.summary}</p>
                        {interview.result?.highlights?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs font-semibold text-stone-500 mb-2">Highlights</p>
                            <ul className="space-y-1">
                              {interview.result.highlights.map((h, i) => (
                                <li key={i} className="text-xs text-stone-600 italic">"{h}"</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {interview.result?.improvements?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs font-semibold text-stone-500 mb-2">Areas to improve</p>
                            <ul className="space-y-1">
                              {interview.result.improvements.map((h, i) => (
                                <li key={i} className="text-xs text-stone-600">• {h}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------
function InfoBox({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-3">
      <p className="text-[10px] uppercase tracking-widest text-stone-400 font-mono">{label}</p>
      <p className="text-sm text-stone-800 mt-0.5 truncate">{value}</p>
    </div>
  );
}

function ScoreCard({ label, score }) {
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-3 text-center">
      <p className="font-display text-2xl" style={{ color: scoreColor(score) }}>{score || "—"}</p>
      <p className="text-[10px] uppercase tracking-widest text-stone-400 font-mono mt-1">{label}</p>
    </div>
  );
}

function Tag({ label, value, present }) {
  if (!present) {
    return (
      <span className="px-2 py-0.5 rounded-full bg-stone-100 border border-stone-200 text-stone-500 text-[11px] font-mono uppercase tracking-widest">
        {label}: —
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-mono uppercase tracking-widest">
      {label}: {value}
    </span>
  );
}

function scoreColor(s) {
  if (!s) return "#78716C";
  if (s >= 80) return "#15803D";
  if (s >= 60) return "#C2410C";
  return "#78716C";
}
